import * as React from "react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { Toast } from "../../../src/apidefs";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";

type ToastState = {
  type?: "error" | "success";
  message?: string;
  redirect?: string;
  refresh?: boolean;
  pushRedirect?: boolean;
};

type ToastSetter = (toast: ToastState) => void;

let setToastState: ToastSetter | undefined;
let pendingToast: Toast | undefined;
let fallbackToastEl: HTMLDivElement | undefined;

const removeFallbackToast = (): void => {
  if (fallbackToastEl?.parentElement) {
    fallbackToastEl.parentElement.removeChild(fallbackToastEl);
  }
  fallbackToastEl = undefined;
};

const renderFallbackToast = (toast: Toast): void => {
  if (typeof document === "undefined") return;
  if (!fallbackToastEl) {
    fallbackToastEl = document.createElement("div");
    fallbackToastEl.style.position = "fixed";
    fallbackToastEl.style.top = "40px";
    fallbackToastEl.style.left = "0";
    fallbackToastEl.style.width = "100%";
    fallbackToastEl.style.padding = "16px 20px";
    fallbackToastEl.style.fontSize = "20px";
    fallbackToastEl.style.fontWeight = "300";
    fallbackToastEl.style.zIndex = "500";
    fallbackToastEl.style.boxSizing = "border-box";
    fallbackToastEl.style.color = "white";
    fallbackToastEl.style.background = "#e65050";
    document.body.appendChild(fallbackToastEl);
  }

  fallbackToastEl.textContent = toast.msg;
};

export const showToast = (toast: Toast): void => {
  if (setToastState) {
    setToastState({
      message: toast.msg,
      type: toast.type,
      redirect: toast.redirect,
      refresh: toast.refresh,
      pushRedirect: toast.push,
    });
    removeFallbackToast();
    return;
  }

  // If the toast display hasn't mounted yet, keep the toast so it can be shown
  // as soon as the component registers its setter.
  pendingToast = toast;
  renderFallbackToast(toast);
};

export const ToastDisplay = (): JSX.Element => {
  const history = useHistory();
  const [toast, setToast] = useState<ToastState>({});

  useEffect(() => {
    setToastState = setToast;

    // Flush any toast that fired before the component mounted.
    if (pendingToast) {
      setToast({
        message: pendingToast.msg,
        type: pendingToast.type,
        redirect: pendingToast.redirect,
        refresh: pendingToast.refresh,
        pushRedirect: pendingToast.push,
      });
      pendingToast = undefined;
      removeFallbackToast();
    }

    return () => {
      if (setToastState === setToast) {
        setToastState = undefined;
      }
      removeFallbackToast();
    };
  }, []);

  useEffect(() => {
    if (!toast.redirect) {
      return;
    }

    const { redirect, refresh, pushRedirect } = toast;
    setToast((prev) => ({ ...prev, redirect: undefined, refresh: undefined }));

    if (redirect === window.location.pathname || refresh) {
      window.location.reload();
      return;
    }

    if (pushRedirect) {
      history.push(redirect);
    } else {
      history.replace(redirect);
    }
  }, [history, toast.redirect, toast.refresh, toast.pushRedirect]);

  if (!toast.message) {
    return <div />;
  }

  const hide = (e: React.MouseEvent) => {
    e.preventDefault();
    setToast({});
  };

  const isSuccess = toast.type === "success";

  return (
    <div className="fixed left-0 top-12 z-[500] flex w-full justify-center px-4">
      <div
        className={`flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg ${
          isSuccess
            ? "border border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-900/80 dark:text-emerald-200"
            : "border border-red-200 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-900/80 dark:text-red-200"
        }`}
      >
        {isSuccess ? (
          <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-emerald-500 dark:text-emerald-400" />
        ) : (
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-red-500 dark:text-red-400" />
        )}
        <span className="text-sm font-medium">{toast.message}</span>
        <button
          onClick={hide}
          className={`ml-2 rounded-md p-1 transition ${
            isSuccess
              ? "text-emerald-500 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-800/50"
              : "text-red-500 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-800/50"
          }`}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
